import { Box, Chip, Stack, Typography } from "@mui/material";

import type { ResponsiveDetailSection } from "../../../components/common/ResponsiveDetailSections";
import {
    formatChannelLabel,
    formatSourceLabel,
    getSeverityChipStyles
} from "../../../lib/presentation";
import type { B2bRow, DataQualityRow, MasterProfile, OrderRow } from "../../../types";

export const orderChannelFilters = ["Всички", "POS", "WooCommerce", "Expo"] as const;

export function getOrderNextAction(order: OrderRow, issueCount: number, hasLinkedProfile: boolean) {
    if (issueCount > 0)
        return "Провери отворените проблеми по поръчката, преди да я използваш за анализ, атрибуция или клиентски профил.";

    if (order.status === "Нова")
        return "Потвърди подаването към търговията на едро и виж дали всички данни за клиента и кампанията са налични.";

    if (order.status === "Подготвя се")
        return "Следи склада и логистиката, за да няма разминаване между поръчката, наличността и статуса в системите.";

    if (order.status === "Натоварена")
        return "Потвърди движението по доставката и затвори атрибуцията към кампанията, ако е приложимо.";

    if (order.status === "Изпратена")
        return "Потвърди движението по доставката и затвори атрибуцията към кампанията, ако е приложимо.";

    if (!hasLinkedProfile)
        return "Поръчката е доставена, но още няма ясен клиентски профил. Провери дали може да се свърже с лоялност или фирмен запис.";

    return "Поръчката е готова за анализ на поведение, стойност и повторна активност на клиента.";
}

export function buildOrderDetailSections({
                                             business,
                                             hiddenIssueCount,
                                             issues,
                                             nextAction,
                                             order,
                                             profile,
                                             visibleIssues
                                         }: {
    business: B2bRow | null;
    hiddenIssueCount: number;
    issues: DataQualityRow[];
    nextAction: string;
    order: OrderRow;
    profile: MasterProfile | null;
    visibleIssues: DataQualityRow[];
}): ResponsiveDetailSection[] {
    return [
        {
            id: "sync",

            title: "Синхрон и кампания",

            summary: `${order.sync} · ${order.campaign}`,
            content: (
                <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                        Синхрон: {order.sync}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Кампания: {order.campaign}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Канал: {formatChannelLabel(order.channel)}
                    </Typography>
                </Stack>
            )
        },

        ...(profile
            ? [
                {
                    id: "profile",
                    title: "Свързан клиентски профил",
                    summary: `${profile.profileId} · ${profile.consent}`,
                    content: (
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Профил: {profile.profileId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Червена карта: {profile.loyaltyCard}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Канал: {profile.consent}
                            </Typography>
                        </Stack>
                    )
                } satisfies ResponsiveDetailSection
            ]
            : []),

        ...(business
            ? [
                {
                    id: "business",
                    title: "Свързан фирмен профил",
                    summary: `${business.eik} · ${business.priceList}`,
                    content: (
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                ЕИК: {business.eik}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Ценова листа: {business.priceList}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Условия за плащане: {business.paymentTerms}
                            </Typography>
                        </Stack>
                    )
                } satisfies ResponsiveDetailSection
            ]
            : []),

        {
            id: "issues",
            title: `Проблеми и проверки (${issues.length})`,
            summary:
                issues.length > 0
                    ? `${issues[0].issue}${issues.length > 1 ? ` и още ${issues.length - 1}` : ""}`
                    : "Няма отворени проблеми за избраната поръчка.",
            content: (
                <Stack spacing={1.25}>
                    {issues.length > 0 ? (
                        visibleIssues.map((issue) => (
                            <Box key={`${issue.entity}-${issue.issue}`}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        justifyContent: "space-between",
                                        alignItems: "flex-start"
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {issue.issue}ResponsiveDetailSection
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {issue.displayEntity ?? issue.entity}
                                            {issue.source
                                                ? ` · ${formatSourceLabel(issue.source)}`
                                                : ""}
                                        </Typography>
                                    </Box>

                                    <Chip
                                        label={issue.severity}
                                        size="small"
                                        sx={{
                                            fontWeight: 700,
                                            ...getSeverityChipStyles(issue.severity)
                                        }}
                                    />
                                </Stack>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Няма отворени проблеми за избраната поръчка.
                        </Typography>
                    )}

                    {hiddenIssueCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Още {hiddenIssueCount} проблема са налични в пълния списък.
                        </Typography>
                    )}
                </Stack>
            )
        },

        {
            id: "next-action",
            title: "Препоръчано следващо действие",
            summary: nextAction,
            content: (
                <Typography variant="body2" color="text.secondary">
                    {nextAction}
                </Typography>
            )
        }
    ];
}
